/**
 * useLiveInterviewMeeting — the "meeting" layer on top of the raw WebRTC
 * call (useLiveInterviewCall): microphone/camera toggles, screen sharing,
 * and floating emoji reactions, plus the shared, per-participant view of all
 * of the above that MeetingStage renders from.
 *
 * Used by BOTH room pages (RecruiterRoomPage, LiveInterviewRoomPage) so the
 * two sides behave identically — they differ only in how they acquire media
 * and what side panel they put next to the stage.
 *
 * TRANSPORT: everything here rides the EXISTING signaling WebSocket as
 * application-level broadcast messages (see
 * backend/api/v2/endpoints/live_interview_signaling.py — a message with no
 * "to" is fanned out to every other connection in the room, with "from"
 * stamped server-side), exactly the way `show-question`/`chat` already do.
 * No backend change was needed and none was made; two new message types were
 * added on the client side only:
 *
 *   {type: "media-state", mic_on, camera_on, sharing}  — broadcast whenever
 *       this participant mutes/unmutes, turns their camera on/off, or
 *       starts/stops presenting, AND re-broadcast whenever a new peer joins
 *       (a peer that arrives later missed every earlier announcement, and
 *       none of this is recoverable from the media stream itself).
 *   {type: "reaction", emoji}                          — ephemeral; nothing
 *       is persisted anywhere, it just floats up and fades out.
 *
 * Two further app messages ride the same channel but are owned by the room
 * PAGES rather than this hook (it only passes them through to onAppMessage):
 *
 *   {type: "verbal-text-box-state", enabled}  — sent by the recruiter room to
 *       flip the candidate, for the question currently on screen, between live
 *       browser speech capture and a plain typed box. Needs no backend change
 *       for the same reason media-state didn't: an unrecognised, "to"-less
 *       message is already fanned out to the rest of the room verbatim.
 *   {type: "answer-submitted", question_id}  — sent by the candidate room the
 *       moment its own answer POST succeeds, so the recruiter's room can
 *       re-fetch its question list / scoring summary instead of showing the
 *       question as pending until someone reloads.
 *   {type: "end-interview"} -> {type: "interview-ended"} — the recruiter-only
 *       "End Interview" action. Unlike every other message here this one is
 *       NOT blindly relayed: the server drops it from a non-recruiter socket
 *       and re-emits it to the whole room (sender included) as
 *       "interview-ended". See live_interview_signaling.py.
 *
 * IDENTITY: `welcome` and `peer-joined` carry each peer's SERVER-resolved
 * role and real display name (a direct users.id lookup, not a JWT claim), so
 * peerStates[peerId].displayName is a person's actual name where one exists
 * and undefined otherwise — never a role word standing in for one.
 *
 * WHY A SEPARATE "sharing" SIGNAL AT ALL: screen sharing is sent as a SECOND
 * track alongside the camera (see useLiveInterviewCall's file header —
 * startScreenShare/stopScreenShare), not a replacement, so both arrive as
 * two independent MediaStreams with no marker of their own saying which is
 * "the screen." `media-state`'s `sharing`/`screen_stream_id` fields are that
 * marker: they tell every peer's useLiveInterviewCall which stream id to
 * treat as the screen (so it can tell it apart from the camera regardless of
 * arrival order) and tell the UI which participant's screen to promote to
 * the main stage.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useLiveInterviewCall,
  type LiveInterviewConnectionState,
  type LiveInterviewIceServer,
  type LiveInterviewNetworkQuality,
  type RemoteTile,
} from './useLiveInterviewCall'

export const REACTION_EMOJIS = ['👍', '👏', '🎉', '😀', '🤔', '❤️'] as const

// How long a floating reaction stays on screen before it is dropped from
// state. Must stay in step with MeetingStage's float-and-fade animation
// duration — the animation is what the viewer actually sees; this is just
// the cleanup that stops the list growing without bound.
export const REACTION_LIFETIME_MS = 2500

export interface FloatingReaction {
  id: string
  emoji: string
  /** 'local' for this participant, otherwise the sender's peer id. */
  peerId: string
  /** 0-100, a random horizontal offset so simultaneous reactions don't stack. */
  offsetPercent: number
}

export interface PeerMediaState {
  micOn: boolean
  cameraOn: boolean
  sharing: boolean
  /**
   * 'recruiter' | 'candidate'. Preferred from the SERVER's own determination
   * (the `role` on `welcome`/`peer-joined`, which only a verified bearer
   * token ever sets to "recruiter"); a peer's self-declared `role` on its
   * media-state announcement is only used as a fallback for a peer the
   * server told us nothing about, and never overwrites a server value.
   */
  role?: string
  /**
   * The peer's REAL name, resolved server-side by a direct users.id lookup
   * (see live_interview_signaling.py's _resolve_display_name). Undefined for
   * the candidate (no user row) and for any peer whose lookup found nothing
   * — the caller labels those, rather than this pretending a role word is a
   * name.
   */
  displayName?: string
  /** True once the server has told us who this peer is. */
  identified?: boolean
}

const DEFAULT_PEER_STATE: PeerMediaState = { micOn: true, cameraOn: true, sharing: false }

/**
 * The "X left the meeting" text for a `peer-left` message, or null when no
 * notice should be shown at all.
 *
 * Shared by BOTH room pages so the two sides word it identically and, more
 * importantly, apply the SAME suppression rule. The relay sends `peer-left`
 * for two quite different reasons (see live_interview_signaling.py):
 *
 *  - a genuine departure (the participant navigated away, or their socket
 *    dropped) — `is_reconnect: false`, and worth telling the room about;
 *  - the eviction of a participant's own stale connection during their
 *    reconnect — `is_reconnect: true`, immediately followed by that same
 *    person's `peer-joined`. Announcing "X left" there and retracting it a
 *    heartbeat later would be worse than saying nothing, so it returns null.
 *
 * Either way the TILE still goes — that is `closePeer`'s job in
 * useLiveInterviewCall and is deliberately untouched by this. This decides
 * only whether a human gets told.
 *
 * The name comes from the server's own resolved identity on the message
 * rather than from local `peerStates`, which the hook has already begun
 * clearing for this peer by the time a page sees the message. Candidates have
 * no user row and so no resolved name; they get a role-shaped label, never an
 * invented name.
 */
export function peerLeftNotice(message: Record<string, any>): string | null {
  if (message?.type !== 'peer-left') return null
  if (message.is_reconnect === true) return null
  const displayName = typeof message.display_name === 'string' ? message.display_name.trim() : ''
  if (displayName) return `${displayName} left the meeting.`
  if (message.role === 'candidate') return 'The candidate left the meeting.'
  if (message.role === 'recruiter') return 'The interviewer left the meeting.'
  return 'A participant left the meeting.'
}

interface UseLiveInterviewMeetingOptions {
  sessionId: string | null | undefined
  localStream: MediaStream | null
  iceServers: LiveInterviewIceServer[] | null
  enabled: boolean
  role?: 'recruiter' | 'candidate'
  authToken?: string | null
  /**
   * Server-configured signaling-reconnect budget, passed straight through to
   * useLiveInterviewCall — see its RECONNECTION header section.
   */
  reconnectTimeoutMs?: number
  /** Non-meeting app messages (chat, show-question, …) are passed through. */
  onAppMessage?: (message: Record<string, any>) => void
}

export interface LiveInterviewMeeting {
  remoteTiles: RemoteTile[]
  connected: boolean
  /**
   * Whether the signaling connection is up, retrying, or has given up. Note
   * this is NOT the inverse of `connected`: 'reconnecting' and 'disconnected'
   * both have `connected === false`, and the meeting controls distinguish
   * them (a spinner-ish "Reconnecting…" vs. a hard "Disconnected").
   */
  connectionState: LiveInterviewConnectionState
  /** Worst-peer WebRTC media quality while signaling is up. */
  networkQuality: LiveInterviewNetworkQuality
  ownPeerId: string | null
  sendAppMessage: (message: Record<string, any>) => void
  micOn: boolean
  cameraOn: boolean
  toggleMic: () => void
  toggleCamera: () => void
  /** The local screen-capture stream while presenting, else null. */
  screenStream: MediaStream | null
  sharing: boolean
  screenShareSupported: boolean
  toggleScreenShare: () => void
  /** 'local' | a remote peer id | null — whose stream owns the main stage. */
  presenterId: string | null
  peerStates: Record<string, PeerMediaState>
  /**
   * This participant's own server-resolved name (recruiters only — the
   * candidate has no user row to name). Null until `welcome` arrives, and
   * null forever for an unnamed participant; callers fall back to "You".
   */
  ownDisplayName: string | null
  reactions: FloatingReaction[]
  sendReaction: (emoji: string) => void
}

export function useLiveInterviewMeeting({
  sessionId, localStream, iceServers, enabled, role, authToken, reconnectTimeoutMs, onAppMessage,
}: UseLiveInterviewMeetingOptions): LiveInterviewMeeting {
  const [micOn, setMicOn] = useState(true)
  const [cameraOn, setCameraOn] = useState(true)
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null)
  const [peerStates, setPeerStates] = useState<Record<string, PeerMediaState>>({})
  const [ownDisplayName, setOwnDisplayName] = useState<string | null>(null)
  const [reactions, setReactions] = useState<FloatingReaction[]>([])

  const onAppMessageRef = useRef(onAppMessage)
  onAppMessageRef.current = onAppMessage
  const screenStreamRef = useRef<MediaStream | null>(null)
  const reactionTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  // Read inside the signaling callback, which is created once per connection
  // — plain state would be captured stale there.
  const ownStateRef = useRef<PeerMediaState>({ ...DEFAULT_PEER_STATE })
  // Stable indirection so that same callback can announce this
  // participant's state without capturing a stale sendAppMessage. Declared
  // before the call below because the callback references it; its body is
  // (re)assigned right after, once sendAppMessage exists.
  const sendStateRef = useRef<() => void>(() => {})
  // Same indirection, for the exclusive-screen-share rule below — stopSharing
  // is declared later (it needs stopScreenShare, which only exists once
  // `call` does), so the message handler above it reaches it through this
  // ref rather than a direct (not-yet-existing) reference.
  const stopSharingRef = useRef<() => void>(() => {})

  const sharing = screenStream !== null
  const screenShareSupported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia

  const pushReaction = useCallback((peerId: string, emoji: string) => {
    const reaction: FloatingReaction = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      emoji,
      peerId,
      offsetPercent: Math.round(Math.random() * 70) + 10,
    }
    setReactions((prev) => [...prev, reaction])
    const timer = setTimeout(() => {
      setReactions((prev) => prev.filter((r) => r.id !== reaction.id))
    }, REACTION_LIFETIME_MS)
    reactionTimersRef.current.push(timer)
  }, [])

  const call = useLiveInterviewCall({
    sessionId,
    localStream,
    iceServers,
    enabled,
    role,
    authToken,
    reconnectTimeoutMs,
    onAppMessage: (message) => {
      const type = message?.type
      const from = message?.from
      if (type === 'media-state' && from) {
        // At most one presenter at a time: the relay never echoes a generic
        // broadcast back to its own sender (see live_interview_signaling.py),
        // so any 'sharing: true' received here is always from someone ELSE —
        // if we're presenting too, yield the stage immediately rather than
        // leaving both sides "sharing" and letting presenterId's tie-break
        // silently decide whose screen everyone actually sees.
        if (message.sharing === true && screenStreamRef.current !== null) {
          void stopSharingRef.current()
        }
        // Tells useLiveInterviewCall which of this peer's (possibly two)
        // incoming MediaStream ids is their screen, so it can resolve camera
        // vs. screen correctly regardless of which track arrived first — see
        // useLiveInterviewCall's file header. Cleared (null) the moment they
        // stop sharing, not left to the track's own 'ended' event alone.
        call.setPeerScreenStreamId(
          from,
          message.sharing === true && typeof message.screen_stream_id === 'string' ? message.screen_stream_id : null
        )
        // Merges rather than replaces, so the server-resolved identity this
        // peer already carries (role/displayName from welcome/peer-joined)
        // survives every subsequent mute/camera/present announcement. The
        // self-declared `role` is only a fallback for a peer we were never
        // told about.
        setPeerStates((prev) => {
          const existing = prev[from]
          return {
            ...prev,
            [from]: {
              ...existing,
              micOn: message.mic_on !== false,
              cameraOn: message.camera_on !== false,
              sharing: message.sharing === true,
              role: existing?.identified
                ? existing.role
                : typeof message.role === 'string' ? message.role : existing?.role,
            },
          }
        })
        return
      }
      if (type === 'reaction' && from && typeof message.emoji === 'string') {
        pushReaction(from, message.emoji)
        return
      }
      if (type === 'welcome') {
        setOwnDisplayName(typeof message.display_name === 'string' && message.display_name ? message.display_name : null)
        // Seed everyone already in the room. Their media state is unknown
        // until they re-announce (which they do on seeing our peer-joined),
        // so it starts at the same "assume on" default a tile falls back to.
        const participants: any[] = Array.isArray(message.participants) ? message.participants : []
        if (participants.length > 0) {
          setPeerStates((prev) => {
            const next = { ...prev }
            participants.forEach((p) => {
              if (!p?.peer_id) return
              next[p.peer_id] = {
                ...DEFAULT_PEER_STATE,
                ...next[p.peer_id],
                role: typeof p.role === 'string' ? p.role : next[p.peer_id]?.role,
                displayName: typeof p.display_name === 'string' && p.display_name ? p.display_name : undefined,
                identified: true,
              }
            })
            return next
          })
        }
      }
      if (type === 'peer-left' && message.peer_id) {
        setPeerStates((prev) => {
          if (!(message.peer_id in prev)) return prev
          const next = { ...prev }
          delete next[message.peer_id]
          return next
        })
        // Still passed through below — a page may want to react to it too.
      }
      if (type === 'peer-joined') {
        if (message.peer_id) {
          setPeerStates((prev) => ({
            ...prev,
            [message.peer_id]: {
              ...DEFAULT_PEER_STATE,
              ...prev[message.peer_id],
              role: typeof message.role === 'string' ? message.role : prev[message.peer_id]?.role,
              displayName:
                typeof message.display_name === 'string' && message.display_name ? message.display_name : undefined,
              identified: true,
            },
          }))
        }
        // Re-announce for the newcomer's benefit. Broadcast (rather than
        // targeted) because the relay has no "send to one peer on their
        // behalf" concept beyond `to`, and re-stating known state to peers
        // that already have it is harmless.
        sendStateRef.current()
      }
      onAppMessageRef.current?.(message)
    },
  })

  const { sendAppMessage, startScreenShare, stopScreenShare, ownPeerId } = call

  sendStateRef.current = () => {
    sendAppMessage({
      type: 'media-state',
      mic_on: ownStateRef.current.micOn,
      camera_on: ownStateRef.current.cameraOn,
      sharing: ownStateRef.current.sharing,
      // The MediaStream id this participant's screen track rides on, so
      // every peer's useLiveInterviewCall can tell it apart from the camera
      // stream regardless of which one's track arrives first (see
      // useLiveInterviewCall's file header). Only meaningful while sharing.
      screen_stream_id: ownStateRef.current.sharing && screenStreamRef.current ? screenStreamRef.current.id : null,
      role: role || 'candidate',
    })
  }

  const announce = useCallback((next: Partial<PeerMediaState>) => {
    ownStateRef.current = { ...ownStateRef.current, ...next }
    sendStateRef.current()
  }, [])

  const toggleMic = useCallback(() => {
    const tracks = localStream?.getAudioTracks() || []
    if (tracks.length === 0) return
    const next = !tracks[0].enabled
    tracks.forEach((t) => { t.enabled = next })
    setMicOn(next)
    announce({ micOn: next })
  }, [localStream, announce])

  const toggleCamera = useCallback(() => {
    const tracks = localStream?.getVideoTracks() || []
    if (tracks.length === 0) return
    const next = !tracks[0].enabled
    tracks.forEach((t) => { t.enabled = next })
    setCameraOn(next)
    announce({ cameraOn: next })
  }, [localStream, announce])

  // Stops sending the screen track (a SECOND track alongside the camera —
  // see useLiveInterviewCall's file header) and tears the local capture down.
  // Safe to call when not sharing.
  const stopSharing = useCallback(async () => {
    const stream = screenStreamRef.current
    screenStreamRef.current = null
    setScreenStream(null)
    stream?.getTracks().forEach((t) => t.stop())
    stopScreenShare()
    announce({ sharing: false })
  }, [stopScreenShare, announce])
  stopSharingRef.current = stopSharing

  const toggleScreenShare = useCallback(() => {
    if (screenStreamRef.current) {
      void stopSharing()
      return
    }
    if (!navigator.mediaDevices?.getDisplayMedia) return
    navigator.mediaDevices
      .getDisplayMedia({ video: true, audio: false })
      .then((stream) => {
        const track = stream.getVideoTracks()[0]
        if (!track) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        screenStreamRef.current = stream
        setScreenStream(stream)
        // The browser's own "Stop sharing" bar ends the track without going
        // through our button — mirror that back into app state.
        track.onended = () => { void stopSharing() }
        startScreenShare(track, stream)
        announce({ sharing: true })
      })
      .catch(() => {
        // Cancelling the picker rejects — not an error worth surfacing.
      })
  }, [startScreenShare, stopSharing, announce])

  // Announce once as soon as the socket is up, so peers that were already in
  // the room learn this participant's state without waiting for a toggle.
  useEffect(() => {
    if (!call.connected) return
    sendStateRef.current()
  }, [call.connected])

  useEffect(() => {
    return () => {
      reactionTimersRef.current.forEach(clearTimeout)
      reactionTimersRef.current = []
      screenStreamRef.current?.getTracks().forEach((t) => t.stop())
      screenStreamRef.current = null
    }
  }, [])

  const sendReaction = useCallback((emoji: string) => {
    sendAppMessage({ type: 'reaction', emoji })
    // The relay excludes the sender from a broadcast, so show our own.
    pushReaction('local', emoji)
  }, [sendAppMessage, pushReaction])

  // At most one main stage: this participant wins if they are presenting,
  // otherwise the first remote peer that announced itself as presenting.
  const remoteSharingPeerId = call.remoteTiles
    .map((t) => t.peerId)
    .find((peerId) => peerStates[peerId]?.sharing) || null
  const presenterId = sharing ? 'local' : remoteSharingPeerId

  return {
    remoteTiles: call.remoteTiles,
    connected: call.connected,
    connectionState: call.connectionState,
    networkQuality: call.networkQuality,
    ownPeerId,
    sendAppMessage,
    micOn,
    cameraOn,
    toggleMic,
    toggleCamera,
    screenStream,
    sharing,
    screenShareSupported,
    toggleScreenShare,
    presenterId,
    peerStates,
    ownDisplayName,
    reactions,
    sendReaction,
  }
}
